import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { get, isEmpty, merge, noop } from "lodash";
import { BinaryFn, NullaryFn, UnaryFn } from "@tvg/ts-types/Functional";
import {
  AlertInline,
  breakpoints,
  useMediaQuery,
  useTheme
} from "@tvg/design-system";
import mediator from "@tvg/mediator";

import { DerbyBanner } from "@urp/derby-banner";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { isFdrProduct, isMobileServerSide } from "@tvg/sh-utils/mobileUtils";
import { events as AlchemerEvents } from "@urp/alchemer";
import { resetBetting } from "@tvg/desktop-bet/src/store/actions";
import { RaceProgram, RaceVideoFeedback } from "@tvg/ts-types/Race";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import { useAutoPlayPPVideos } from "@urp/amplitude-experiments";
import parseJSONCapiMessage from "@tvg/utils/capiUtils";
import { bannerProps } from "@tvg/sh-utils/fixedBanner";
import { getGeolocationStatus } from "@tvg/sh-geolocation/src/redux/selectors";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import {
  getAccountNumber,
  getBalance,
  getIsLogged
} from "@urp/store-selectors";
import {
  BetTypeSelectorContainer,
  ProgramPageContainer,
  DerbyBannerStyled
} from "./styled-components";
import {
  getProgramPageMessages,
  getDerbyBannerImageToggle,
  getTrackHeaderEnhacement
} from "./store/selectors";
import {
  useCurrentRaces,
  useBetValidation,
  useRacesBettingInterests,
  useRaceoff,
  useSetUserFavoriteRunners,
  useIsLiveTv
} from "./hooks";
import TrackHeader from "./components/TrackHeader";
import Main from "./components/Main";
import BetTypeSelector from "./components/TrackHeader/BetTypeSelector";
import FeedbackButton from "./components/FeedbackButton";
import ReplayVideoModal from "./components/ReplayVideoModal";
import { isRefunded } from "./utils/raceUtils";
import { openLoginWall } from "./utils/general";
import usePageViewEventDispatch from "./hooks/usePageViewEventDispatch";
import { calculateFavoriteRunner } from "./utils/runnersUtils";
import useDerbyBanner from "./hooks/useDerbyBanner";
import NextBettableRaceAlert from "./components/NextBettableRaceAlert";

interface IVideoProps {
  stream: string;
  currentRace: RaceProgram;
  videoFeedBack: string;
  trackAbbr: string | undefined;
  userBalance?: number;
  isLogged?: boolean;
}

interface Props {
  prevPath?: string;
  onAccountBalanceClick?: NullaryFn<void>;
  openLogin?: NullaryFn<void>;
  openRegister?: NullaryFn<void>;
  onClickVideo?: BinaryFn<string, string | undefined, void>;
  withFixedBanner?: boolean;
  withVideo?: boolean;
  children?: UnaryFn<IVideoProps, Node>;
  fixedBanner?: React.ReactElement;
  onReplayModalCallback?: UnaryFn<boolean, void>;
  nextBettableRaceUrl?: string;
}

export const PPage = ({
  prevPath = "/home",
  withFixedBanner,
  onAccountBalanceClick,
  openLogin,
  openRegister,
  onClickVideo = noop,
  withVideo = false,
  children,
  fixedBanner,
  onReplayModalCallback,
  nextBettableRaceUrl
}: Props) => {
  const dispatch = useDispatch();
  const isLiveTv = useIsLiveTv();
  const isAccountCompliant = useSelector(isAccountCompliantSelector);
  const programPageMessages = useSelector(getProgramPageMessages);
  const betTypeSelectorOffset = useSelector((store) =>
    get(store, "programPage.fixWagerSelector.fixedOffset", 0)
  );
  const hasTrackHeaderEnhacement = useSelector(getTrackHeaderEnhacement);
  const accountNumber: string = useSelector(getAccountNumber);
  const isVerified = useSelector((store) => get(store, "userData.isVerified"));
  const isTermsAccepted = useSelector((store) =>
    get(store, "userData.jurisdiction.accepted")
  );
  const accountCompliantBannerMsg = useSelector((store) =>
    parseJSONCapiMessage(store, "capi.messages.accountCompliantBannerMsg")
  );
  const geolocationBannerMsg = useSelector((store) =>
    parseJSONCapiMessage(store, "capi.messages.geoLocationBannerMsg")
  );
  const geolocationStatus = useSelector(getGeolocationStatus);
  const derbyBannerImageToggle = useSelector(getDerbyBannerImageToggle);

  const [showVideo, setShowVideo] = useState(false);
  const [isMtpHidden, setIsMtpHidden] = useState(false);
  const [openReplayModal, setOpenReplayModal] = useState(false);

  // TODO maybe we don't need all the races in here for live tv page
  const { currentRace, races, loading } = useCurrentRaces();
  const params = useParams<{ trackAbbr: string | undefined }>();
  const location = useLocation();
  useBetValidation(currentRace, isLiveTv);
  const { isStaleData, racesBI, fetchMore } = useRacesBettingInterests(
    currentRace,
    races,
    isAccountCompliant
  );
  useRaceoff(currentRace);
  usePageViewEventDispatch(currentRace, loading);
  useSetUserFavoriteRunners(accountNumber);
  useEffect(
    () =>
      // reset betting on unmount program page
      () => {
        dispatch(resetBetting());
      },
    []
  );

  const { derbyRaceDate, derbyEventStatus, bannerCfg, onBannerCtaClick } =
    useDerbyBanner();

  const isLogged = useSelector(getIsLogged);
  const userBalance = useSelector(getBalance);

  const videoConfig = useAutoPlayPPVideos();
  const theme = useTheme();
  const isMobile =
    useMediaQuery(breakpoints.tablet.max.sm) || isMobileServerSide();

  const onCloseWatchReplay = useCallback(() => {
    setOpenReplayModal(false);

    if (typeof onReplayModalCallback === "function") {
      onReplayModalCallback(false);
    }
  }, [onReplayModalCallback]);

  const racesWithBI = races.map((raceData: RaceProgram): RaceProgram => {
    const biForRace =
      (racesBI || []).find(
        (raceBI: RaceProgram) => raceBI.id === raceData.id
      ) || {};

    return { ...raceData, ...biForRace };
  });

  let currentRaceWithBi = calculateFavoriteRunner(
    racesWithBI.find((raceBI: RaceProgram) => raceBI.id === currentRace.id) ||
      currentRace
  );

  currentRaceWithBi = {
    ...currentRaceWithBi,
    wagerTypes: currentRace.wagerTypes
  };

  const currentRaceLoadingState =
    (loading &&
      !(
        currentRace?.track?.trackCode &&
        currentRace?.track?.trackCode === params.trackAbbr
      )) ||
    isEmpty(currentRace);

  const raceNumberUrl = location.search.split("=")[1];
  const currentRaceUrlId = `${params.trackAbbr}-${raceNumberUrl}`;

  const raceCardLoadingState =
    (loading && !(currentRace.id === currentRaceUrlId)) || isStaleData;

  const nextRaceIndex =
    races.findIndex((race) => race.tvgRaceId === currentRace.tvgRaceId) + 1;
  const nextRace = merge(
    get(racesBI, nextRaceIndex),
    get(races, nextRaceIndex)
  );

  let videoFeedBack = RaceVideoFeedback.NOT_AVAILABLE_NO_REPLAY;
  if (races.length) {
    videoFeedBack = RaceHeaderUtils.getVideoFeedback(
      currentRace,
      races[0],
      races[racesWithBI.length - 1]
    ) as RaceVideoFeedback;
  }

  const onClickWatchReplay = useCallback(() => {
    if (isLogged) {
      if (!isAccountCompliant) {
        onOpenComplianceModal();
        return;
      }

      if (typeof onReplayModalCallback === "function") {
        onReplayModalCallback(true);
      }
      setOpenReplayModal(true);
    } else {
      openLoginWall(() => {
        if (typeof onReplayModalCallback === "function") {
          onReplayModalCallback(true);
        }
        setOpenReplayModal(true);
      });
    }
  }, [onReplayModalCallback, isLogged]);

  const stream = get(currentRaceWithBi, "video.streams[0]", "");

  const raceMtpBelowThreshold =
    currentRace && currentRace.mtp !== undefined
      ? currentRace.mtp <= videoConfig.mtpThreshold
      : false;

  const videoProps = {
    stream,
    // This code prevents the video player to autoplay replay video, this was moved out
    // of this header
    currentRace:
      isMobile && hasTrackHeaderEnhacement
        ? {
            ...currentRaceWithBi,
            video: {
              ...currentRaceWithBi?.video,
              replayFileName: null,
              hasReplay: false
            }
          }
        : currentRaceWithBi,
    videoFeedBack,
    trackAbbr: currentRaceWithBi.track?.trackCode,
    isURP: isMobile,
    withFixedBanner,
    feedback: videoFeedBack,
    channel: false,
    isLogged,
    userBalance
  };

  mediator.base.subscribe("VIDEO:CLOSE", () => {
    if (isMobile && hasTrackHeaderEnhacement) {
      setShowVideo(false);
    }
  });

  useEffect(() => {
    const shouldAutoPlay =
      videoConfig.autoPlay &&
      raceMtpBelowThreshold &&
      videoProps.videoFeedBack === RaceVideoFeedback.AVAILABLE_NO_REPLAY;

    if (
      !currentRaceLoadingState &&
      (shouldAutoPlay || location.state?.showVideo)
    ) {
      setShowVideo(true);
      onClickVideo(videoProps.videoFeedBack, "auto_play");
    }
  }, [
    currentRaceLoadingState,
    location.state?.showVideo,
    videoProps.videoFeedBack,
    videoConfig.autoPlay,
    raceMtpBelowThreshold
  ]);

  const bannerPropsValues = bannerProps(
    dispatch,
    {
      isVerified,
      isLogged,
      isTermsAccepted
    },
    accountCompliantBannerMsg,
    geolocationBannerMsg,
    geolocationStatus
  );
  return (
    <>
      <ProgramPageContainer isFdr={isFdrProduct()}>
        {!isLiveTv && (
          <TrackHeader
            prevPath={prevPath}
            races={racesWithBI}
            race={currentRaceWithBi}
            isLoading={currentRaceLoadingState}
            setIsMtpHidden={setIsMtpHidden}
            withFixedBanner={withFixedBanner}
            onAccountBalanceClick={onAccountBalanceClick}
            openLogin={openLogin}
            openRegister={openRegister}
            onClickVideo={() => {
              setShowVideo(true);
              onClickVideo(videoProps.videoFeedBack, undefined);
            }}
            refetchBettingInterests={fetchMore}
            isMobile={isMobile}
            fixedBanner={fixedBanner}
            withVideo={withVideo && showVideo}
            isAccountCompliant={isAccountCompliant}
          />
        )}
        {!currentRaceLoadingState &&
          showVideo &&
          withVideo &&
          isFdrProduct() &&
          children &&
          // @ts-ignore
          children(videoProps)}
        <BetTypeSelectorContainer
          isFdr={isFdrProduct()}
          data-qa-label="bet-type-selector-container"
          withVideo={withVideo && showVideo}
          withFixedBanner={withFixedBanner}
          betTypeSelectorOffset={betTypeSelectorOffset}
          isLiveTv={isLiveTv}
          isLogged={isLogged}
          isAccountCompliant={isAccountCompliant}
          hasFixedBannerTvg5={bannerPropsValues}
        >
          <BetTypeSelector
            currentRace={currentRace}
            isLoading={currentRaceLoadingState}
            isMtpHidden={isMtpHidden}
          />

          {/* Render Live TV info that has a available bettable races */}
          <NextBettableRaceAlert
            isLoading={raceCardLoadingState || currentRaceLoadingState}
            nextBettableRaceUrl={nextBettableRaceUrl}
            currentRaceStatus={currentRace?.status?.code}
          />

          {isRefunded(currentRace) && (
            <AlertInline
              message={get(
                programPageMessages,
                "refunded.betTypeAlertMessage",
                ""
              )}
              qaLabel="race-refunded-message"
              variant="warning"
              type="fixed"
              paddingY={isMobile ? 0 : "space-6"}
            />
          )}
        </BetTypeSelectorContainer>

        {derbyBannerImageToggle && isMobile && bannerCfg && (
          <DerbyBannerStyled theme={theme}>
            <DerbyBanner
              raceStartDate={derbyRaceDate}
              eventStatus={derbyEventStatus}
              bannerCfg={bannerCfg}
              onCtaClick={onBannerCtaClick}
            />
          </DerbyBannerStyled>
        )}

        <Main
          race={currentRaceWithBi}
          races={racesWithBI}
          isLoading={raceCardLoadingState}
          nextRace={nextRace}
          onClickWatchReplay={onClickWatchReplay}
        />
        {(hasTrackHeaderEnhacement || isLiveTv) && (
          <ReplayVideoModal
            isOpen={openReplayModal}
            currentRace={currentRace}
            userLoggedIn={isLogged}
            onClose={onCloseWatchReplay}
          >
            <Main
              race={currentRaceWithBi}
              races={racesWithBI}
              isLoading={raceCardLoadingState}
              nextRace={nextRace}
              isReplayModal
            />
          </ReplayVideoModal>
        )}
      </ProgramPageContainer>
      {!isMobile && !isLiveTv && (
        <FeedbackButton
          apptentiveEvent={AlchemerEvents.showNewProgramPageFeedbackSurvey}
        />
      )}
    </>
  );
};

export default PPage;
